import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DuplicateIcon from "@mui/icons-material/ContentCopy";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";

import ResponsiveGrid from "../grid/responsiveGrid";

import { GridActionsCellItem, GridToolbarContainer } from "@mui/x-data-grid";
import { useUserAuth } from "../contexts/authContext";
import { CustomPagination } from "../customPagination";

import { ConvertFeetToMeters, ConvertISOToDate, ConvertMetersToFeet, ConvertNauticalMilesToMeters, ConvertMetersToNauticalMiles } from "../util";

export function ZCASettings() {
    const { user, handleFailedFetch, getOrganizationByID, setSnackbar } = useUserAuth();
    const [configs, setConfigs] = useState([]);
    const [editRow, setEditRow] = useState({});
    const [zcaDialogOpen, setZcaDialogOpen] = useState(false);

    const zcaColumns = [
        {
            field: "org_default",
            headerName: "Default",
            width: 80,
            align: "center",
            headerAlign: "center",
            disableColumnMenu: true,
            sortable: false,
            display: "flex",
            renderCell: (cellValues) => {
                return cellValues.formattedValue ? (
                    <Tooltip title="Default">
                        <CheckCircleIcon color="success" fontSize="medium" />
                    </Tooltip>
                ) : (
                    <Tooltip title="">
                        <RadioButtonUncheckedIcon color="disabled" fontSize="medium" />
                    </Tooltip>
                );
            }
        },
        {
            field: "name",
            headerName: "Name",
            width: 100,
            flex: 1,
            editable: false
        },
        {
            field: "config_uuid",
            headerName: "UUID",
            width: 100,
            flex: 1,
            editable: false
        },
        {
            field: "organization_id",
            headerName: "Org",
            width: 100,
            flex: 1,
            editable: false,
            valueGetter: (value) => {
                const org = getOrganizationByID(value);
                return org ? org.name : "";
            }
        },
        {
            field: "date_created",
            headerName: "Date Created",
            width: 100,
            flex: 1,
            editable: false,
            valueFormatter: (value) => `${ConvertISOToDate(value)}`
        },
        {
            field: "date_updated",
            headerName: "Date Updated",
            width: 100,
            flex: 1,
            editable: false,
            valueFormatter: (value) => `${ConvertISOToDate(value)}`
        },
        {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            width: 150,
            getActions: (params) => {
                return [
                    <GridActionsCellItem
                        key={params.id}
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary"
                        onClick={() => handleEditClick(params.row)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        key={params.id}
                        icon={<DuplicateIcon />}
                        label="Duplicate"
                        className="textPrimary"
                        onClick={() => handleDuplicateClick(params.row)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        key={params.id}
                        icon={<DeleteIcon />}
                        label="Delete"
                        className="textPrimary"
                        onClick={() => handleDeleteClick(params.row)}
                        color="inherit"
                    />
                ];
            }
        }
    ];

    useEffect(() => {
        fetchData();
        return () => setConfigs([]);
    }, []);

    const fetchData = async () => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        await fetch("/api/zca_configs/get", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((data) => setConfigs(data))
            .catch((err) => handleFailedFetch(err));
    };

    const handleEditClick = (row) => {
        setEditRow(row);
        setZcaDialogOpen(true);
    };

    const handleDuplicateClick = async (row) => {
        const duplicateRow = {
            id: 0,
            config_uuid: uuidv4(),
            organization_id: row.organization_id,
            name: row.name,
            org_default: false,
            min_alert_time_sec: row.min_alert_time_sec,
            ownship_expire_sec: row.ownship_expire_sec,
            surveillance_expire_sec: row.surveillance_expire_sec,
            secondary_shutoff_sec: row.secondary_shutoff_sec,
            lat_bound_range_deg: row.lat_bound_range_deg,
            lon_bound_range_deg: row.lon_bound_range_deg,
            alt_bound_range_m: row.alt_bound_range_m,
            m_of_n: row.m_of_n,
            alert_volumes_warning_radar_horz_m: row.alert_volumes_warning_radar_horz_m,
            alert_volumes_warning_radar_vert_m: row.alert_volumes_warning_radar_vert_m,
            alert_volumes_warning_adsb_horz_m: row.alert_volumes_warning_adsb_horz_m,
            alert_volumes_warning_adsb_vert_m: row.alert_volumes_warning_adsb_vert_m,
            alert_volumes_warning_uas_horz_m: row.alert_volumes_warning_uas_horz_m,
            alert_volumes_warning_uas_vert_m: row.alert_volumes_warning_uas_vert_m,
            alert_volumes_warning_projection_sec: row.alert_volumes_warning_projection_sec,
            alert_volumes_caution_radar_horz_m: row.alert_volumes_caution_radar_horz_m,
            alert_volumes_caution_radar_vert_m: row.alert_volumes_caution_radar_vert_m,
            alert_volumes_caution_adsb_horz_m: row.alert_volumes_caution_adsb_horz_m,
            alert_volumes_caution_adsb_vert_m: row.alert_volumes_caution_adsb_vert_m,
            alert_volumes_caution_uas_horz_m: row.alert_volumes_caution_uas_horz_m,
            alert_volumes_caution_uas_vert_m: row.alert_volumes_caution_uas_vert_m,
            alert_volumes_caution_projection_sec: row.alert_volumes_caution_projection_sec,
            updated_user_id: user.id,
            disable_alert_on_positive_range_rate: row.disable_alert_on_positive_range_rate
        };
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(duplicateRow)
        };
        await fetch("/api/zca_configs/insertupdate", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => {
                fetchData();
                setSnackbar({ children: "Configuration has been duplicated.", severity: "success" });
            })
            .catch((err) => handleFailedFetch(err));
    };

    const handleDeleteClick = (row) => {
        if (!confirm("Are you sure you want to Delete this Configuration?")) {
            return;
        }

        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" }
        };
        fetch(`/api/zca_configs/delete/${row.id}`, requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => fetchData())
            .catch((err) => handleFailedFetch(err));
        alert("Advise: These changes will not impact active ZCA sessions.");
    };

    const handleZcaDialogClose = () => {
        setZcaDialogOpen(false);
        fetchData();
    };

    return (
        <Grid size={16} sx={{ maxWidth: "100% !important", p: 3, width: "100%" }}>
            <Typography component="h2" variant="h6" color="#6b778c" noWrap sx={{ flexGrow: 1 }}>
                Manage ZCA Configs
            </Typography>
            <Paper sx={{ p: 2, mt: 2, display: "flex", flexDirection: "column", width: "100%" }}>
                <ResponsiveGrid
                    rows={configs}
                    columns={zcaColumns}
                    getRowId={(row) => row.id}
                    autoHeight
                    disableRowSelectionOnClick
                    disableVirtualization
                    slots={{ toolbar: AddToolbar, pagination: CustomPagination }}
                    slotProps={{ toolbar: { setEditRow, setZcaDialogOpen } }}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                />
                {zcaDialogOpen ? (
                    <ZCADialog configs={configs} row={editRow} zcaDialogOpen={zcaDialogOpen} handleZcaDialogClose={handleZcaDialogClose} />
                ) : (
                    <></>
                )}
            </Paper>
        </Grid>
    );
}

export function ZCADialog(props) {
    const { user, getOrganizationByID, handleFailedFetch, snackbar, setSnackbar } = useUserAuth();
    const isDesktop = useMediaQuery("(min-width:900px)");

    const [zca, setZca] = useState(props.row);
    const [uuid, setUuid] = useState("");
    const [organizationId, setOrganizationId] = useState(null);
    const [name, setName] = useState("");
    const [orgDefault, setOrgDefault] = useState(false);
    const [organization, setOrganization] = useState(null);
    const [minAlertTime, setMinAlertTime] = useState("");
    const [ownshipExpire, setOwnshipExpire] = useState("");
    const [surveillanceExpire, setSurveillanceExpire] = useState("");
    const [secondaryShutoff, setSecondaryShutoff] = useState("");
    const [latBoundRange, setLatBoundRange] = useState("");
    const [lonBoundRange, setLonBoundRange] = useState("");
    const [altBoundRange, setAltBoundRange] = useState("");
    const [noiseFilterM, setNoiseFilterM] = useState("");
    const [noiseFilterN, setNoiseFilterN] = useState("");
    const [warningRadarHorz, setWarningRadarHorz] = useState("");
    const [warningRadarVert, setWarningRadarVert] = useState("");
    const [warningAdsbHorz, setWarningAdsbHorz] = useState("");
    const [warningAdsbVert, setWarningAdsbVert] = useState("");
    const [warningUasHorz, setWarningUasHorz] = useState("");
    const [warningUasVert, setWarningUasVert] = useState("");
    const [warningProjection, setWarningProjection] = useState("");
    const [cautionRadarHorz, setCautionRadarHorz] = useState("");
    const [cautionRadarVert, setCautionRadarVert] = useState("");
    const [cautionAdsbHorz, setCautionAdsbHorz] = useState("");
    const [cautionAdsbVert, setCautionAdsbVert] = useState("");
    const [cautionUasHorz, setCautionUasHorz] = useState("");
    const [cautionUasVert, setCautionUasVert] = useState("");
    const [cautionProjection, setCautionProjection] = useState("");
    const [disableAlertOnPositiveRangeRate, setDisableAlertOnPositiveRangeRate] = useState(false);

    useEffect(() => {
        setUuid(zca.config_uuid);
        setOrganizationId(zca.organization_id);
        setName(zca.name);
        setOrgDefault(zca.org_default);
        setOrganization(getOrganizationByID(zca.organization_id));
        setMinAlertTime(zca.min_alert_time_sec);
        setOwnshipExpire(zca.ownship_expire_sec);
        setSurveillanceExpire(zca.surveillance_expire_sec);
        setSecondaryShutoff(zca.secondary_shutoff_sec);
        setLatBoundRange(zca.lat_bound_range_deg);
        setLonBoundRange(zca.lon_bound_range_deg);
        setNoiseFilterM(zca.m_of_n[0]);
        setNoiseFilterN(zca.m_of_n[1]);

        const altBoundRangeFt = ConvertMetersToFeet(zca.alt_bound_range_m);
        setAltBoundRange(altBoundRangeFt);

        const warningRadarHorzNm = ConvertMetersToNauticalMiles(zca.alert_volumes_warning_radar_horz_m);
        setWarningRadarHorz(warningRadarHorzNm);
        setWarningProjection(zca.alert_volumes_warning_projection_sec);

        const warningRadarVertFt = ConvertMetersToFeet(zca.alert_volumes_warning_radar_vert_m);
        setWarningRadarVert(warningRadarVertFt);

        const warningAdsbHorzNm = ConvertMetersToNauticalMiles(zca.alert_volumes_warning_adsb_horz_m);
        setWarningAdsbHorz(warningAdsbHorzNm);

        const warningAdsbVertFt = ConvertMetersToFeet(zca.alert_volumes_warning_adsb_vert_m);
        setWarningAdsbVert(warningAdsbVertFt);

        const warningUasHorzNm = ConvertMetersToNauticalMiles(zca.alert_volumes_warning_uas_horz_m);
        setWarningUasHorz(warningUasHorzNm);

        const warningUasVertFt = ConvertMetersToFeet(zca.alert_volumes_warning_uas_vert_m);
        setWarningUasVert(warningUasVertFt);

        const cautionRadarHorzNm = ConvertMetersToNauticalMiles(zca.alert_volumes_caution_radar_horz_m);
        setCautionRadarHorz(cautionRadarHorzNm);

        const cautionRadarVertFt = ConvertMetersToFeet(zca.alert_volumes_caution_radar_vert_m);
        setCautionRadarVert(cautionRadarVertFt);

        const cautionAdsbHorzNm = ConvertMetersToNauticalMiles(zca.alert_volumes_caution_adsb_horz_m);
        setCautionAdsbHorz(cautionAdsbHorzNm);

        const cautionAdsbVertFt = ConvertMetersToFeet(zca.alert_volumes_caution_adsb_vert_m);
        setCautionAdsbVert(cautionAdsbVertFt);

        const cautionUasHorzNm = ConvertMetersToNauticalMiles(zca.alert_volumes_caution_uas_horz_m);
        setCautionUasHorz(cautionUasHorzNm);

        const cautionUasVertFt = ConvertMetersToFeet(zca.alert_volumes_caution_uas_vert_m);
        setCautionUasVert(cautionUasVertFt);
        setCautionProjection(zca.alert_volumes_caution_projection_sec);

        const disableAlertOnPosRangeRate = zca.disable_alert_on_positive_range_rate;
        setDisableAlertOnPositiveRangeRate(disableAlertOnPosRangeRate);
    }, [zca]);

    const handleIntegerFieldChange = (e, setField) => {
        const value = e.target.value;
        if (/^\d*$/.test(value)) {
            setField(value);
        }
    };

    const handleSave = async () => {
        const validateNumericalFields = (...fields) => {
            return fields.every((field) => {
                const value = parseFloat(field);
                return !isNaN(value) && value >= 0;
            });
        };
        const validateAlertFields = (...alertFields) => {
            for (let i = 0; i + 1 < alertFields.length; i += 2) {
                const warning = parseFloat(alertFields[i]);
                const caution = parseFloat(alertFields[i + 1]);
                if (warning > caution) {
                    return false;
                }
            }
            return true;
        };

        if (!name) {
            return setSnackbar({ children: "Please fill in the name of the configuration.", severity: "error" });
        } else if (
            orgDefault &&
            props.configs.some((config) => config.config_uuid !== uuid && config.organization_id === organizationId && config.org_default)
        ) {
            return setSnackbar({ children: "A default configuration is already set for this organization.", severity: "error" });
        } else if (
            !validateNumericalFields(
                minAlertTime,
                ownshipExpire,
                surveillanceExpire,
                secondaryShutoff,
                altBoundRange,
                noiseFilterM,
                noiseFilterN,
                warningRadarHorz,
                warningRadarVert,
                warningAdsbHorz,
                warningAdsbVert,
                warningUasHorz,
                warningUasVert,
                warningProjection,
                cautionRadarHorz,
                cautionRadarVert,
                cautionAdsbHorz,
                cautionAdsbVert,
                cautionUasHorz,
                cautionUasVert,
                cautionProjection
            )
        ) {
            return setSnackbar({ children: "Please enter valid (non-negative) values for the below fields.", severity: "error" });
        } else if (
            !validateAlertFields(
                warningRadarHorz,
                cautionRadarHorz,
                warningRadarVert,
                cautionRadarVert,
                warningAdsbHorz,
                cautionAdsbHorz,
                warningAdsbVert,
                cautionAdsbVert,
                warningUasHorz,
                cautionUasHorz,
                warningUasVert,
                cautionUasVert
            )
        ) {
            return setSnackbar({ children: "All warning fields must be less than (or equal) to its corresponding caution field.", severity: "error" });
        } else if (parseFloat(noiseFilterM) > parseFloat(noiseFilterN)) {
            return setSnackbar({ children: "Noise filter M must be less than (or equal) to noise filter N.", severity: "error" });
        } else if (
            isNaN(parseFloat(latBoundRange)) ||
            parseFloat(latBoundRange) < -90 ||
            parseFloat(latBoundRange) > 90 ||
            isNaN(parseFloat(lonBoundRange)) ||
            parseFloat(lonBoundRange) < -180 ||
            parseFloat(lonBoundRange) > 180
        ) {
            return setSnackbar({ children: "Latitude must be [-90, 90]. Longitude must be [-180, 180].", severity: "error" });
        }

        const newRow = {
            id: zca.id,
            config_uuid: uuid,
            organization_id: organizationId,
            name: name,
            org_default: orgDefault,
            min_alert_time_sec: minAlertTime,
            ownship_expire_sec: ownshipExpire,
            surveillance_expire_sec: surveillanceExpire,
            secondary_shutoff_sec: secondaryShutoff,
            lat_bound_range_deg: latBoundRange,
            lon_bound_range_deg: lonBoundRange,
            alt_bound_range_m: ConvertFeetToMeters(altBoundRange),
            m_of_n: [noiseFilterM, noiseFilterN],
            alert_volumes_warning_radar_horz_m: ConvertNauticalMilesToMeters(warningRadarHorz),
            alert_volumes_warning_radar_vert_m: ConvertFeetToMeters(warningRadarVert),
            alert_volumes_warning_adsb_horz_m: ConvertNauticalMilesToMeters(warningAdsbHorz),
            alert_volumes_warning_adsb_vert_m: ConvertFeetToMeters(warningAdsbVert),
            alert_volumes_warning_uas_horz_m: ConvertNauticalMilesToMeters(warningUasHorz),
            alert_volumes_warning_uas_vert_m: ConvertFeetToMeters(warningUasVert),
            alert_volumes_warning_projection_sec: warningProjection,
            alert_volumes_caution_radar_horz_m: ConvertNauticalMilesToMeters(cautionRadarHorz),
            alert_volumes_caution_radar_vert_m: ConvertFeetToMeters(cautionRadarVert),
            alert_volumes_caution_adsb_horz_m: ConvertNauticalMilesToMeters(cautionAdsbHorz),
            alert_volumes_caution_adsb_vert_m: ConvertFeetToMeters(cautionAdsbVert),
            alert_volumes_caution_uas_horz_m: ConvertNauticalMilesToMeters(cautionUasHorz),
            alert_volumes_caution_uas_vert_m: ConvertFeetToMeters(cautionUasVert),
            alert_volumes_caution_projection_sec: cautionProjection,
            updated_user_id: user.id,
            disable_alert_on_positive_range_rate: disableAlertOnPositiveRangeRate
        };

        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(newRow)
        };
        await fetch("/api/zca_configs/insertupdate", requestOptions).catch((err) => handleFailedFetch(err));

        handleClose();
        if (zca.id !== 0) {
            alert("Advise: These changes will not impact active ZCA sessions.");
        }
    };

    const handleReset = () => {
        setZca(getDefaultZcaConfig(user, zca.id, zca.config_uuid));
        setSnackbar({ children: "Configuration has been reset!", severity: "success" });
    };

    const handleClose = () => {
        props.handleZcaDialogClose();
    };

    return (
        <Dialog onClose={handleClose} open={props.zcaDialogOpen} maxWidth={isDesktop ? "lg" : "xs"} fullWidth>
            <DialogTitle>{zca.id === 0 ? "Create" : "Edit"} Configuration</DialogTitle>
            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: isDesktop ? "1.35fr 1fr 1fr 1fr" : "1fr", gap: 2 }}>
                        <TextField
                            id="uuid"
                            inputProps={{ "data-testid": "uuid" }}
                            label="UUID"
                            value={uuid}
                            disabled
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                        />
                        <TextField
                            id="organization"
                            inputProps={{ "data-testid": "organization" }}
                            label="Organization"
                            value={organization ? organization.name : "Unknown"}
                            disabled
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                        />
                        <TextField
                            id="name"
                            inputProps={{ "data-testid": "name" }}
                            label="Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            error={snackbar?.severity === "error" && !name}
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    id="defaultConfiguration"
                                    inputProps={{ "data-testid": "defaultConfiguration" }}
                                    checked={orgDefault}
                                    onChange={() => setOrgDefault(!orgDefault)}
                                    sx={{ "& .MuiSvgIcon-root": { fontSize: 30 } }}
                                />
                            }
                            label={<Typography sx={{ fontSize: 15 }}>Default Configuration for Org?</Typography>}
                            sx={{ mx: 0 }}
                        />
                    </Box>
                    <Divider textAlign="left" flexItem>
                        Settings
                    </Divider>
                    <Box sx={{ display: "grid", gridTemplateColumns: isDesktop ? "1fr 1fr 1fr 1fr 1fr" : "1fr", gap: 2 }}>
                        <TextField
                            id="latBoundRange"
                            inputProps={{ "data-testid": "latBoundRange" }}
                            label="Latitude Bound Range"
                            value={latBoundRange}
                            onChange={(e) => setLatBoundRange(e.target.value)}
                            error={
                                snackbar?.severity === "error" &&
                                (isNaN(parseFloat(latBoundRange)) || parseFloat(latBoundRange) < -90 || parseFloat(latBoundRange) > 90)
                            }
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            type="number"
                            onWheel={(e) => e.target.blur()}
                            InputProps={{ endAdornment: <InputAdornment position="end">deg</InputAdornment> }}
                        />
                        <TextField
                            id="lonBoundRange"
                            inputProps={{ "data-testid": "lonBoundRange" }}
                            label="Longitude Bound Range"
                            value={lonBoundRange}
                            onChange={(e) => setLonBoundRange(e.target.value)}
                            error={
                                snackbar?.severity === "error" &&
                                (isNaN(parseFloat(lonBoundRange)) || parseFloat(lonBoundRange) < -180 || parseFloat(lonBoundRange) > 180)
                            }
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            type="number"
                            onWheel={(e) => e.target.blur()}
                            InputProps={{ endAdornment: <InputAdornment position="end">deg</InputAdornment> }}
                        />
                        <TextField
                            id="altBoundRange"
                            inputProps={{ "data-testid": "altBoundRange" }}
                            label="Altitude Bound Range"
                            value={altBoundRange}
                            onChange={(e) => setAltBoundRange(e.target.value)}
                            error={snackbar?.severity === "error" && (isNaN(parseFloat(altBoundRange)) || parseFloat(altBoundRange) < 0)}
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            type="number"
                            onWheel={(e) => e.target.blur()}
                            InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
                        />
                        <TextField
                            id="noiseFilterM"
                            inputProps={{ "data-testid": "noiseFilterM" }}
                            label="Noise Filter M"
                            value={noiseFilterM}
                            onChange={(e) => handleIntegerFieldChange(e, setNoiseFilterM)}
                            error={
                                snackbar?.severity === "error" &&
                                (isNaN(parseFloat(noiseFilterM)) || parseFloat(noiseFilterM) < 0 || parseFloat(noiseFilterM) > parseFloat(noiseFilterN))
                            }
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            autoComplete="off"
                        />
                        <TextField
                            id="noiseFilterN"
                            inputProps={{ "data-testid": "noiseFilterN" }}
                            label="Noise Filter N"
                            value={noiseFilterN}
                            onChange={(e) => handleIntegerFieldChange(e, setNoiseFilterN)}
                            error={
                                snackbar?.severity === "error" &&
                                (isNaN(parseFloat(noiseFilterN)) || parseFloat(noiseFilterN < 0) || parseFloat(noiseFilterM) > parseFloat(noiseFilterN))
                            }
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            autoComplete="off"
                        />
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: isDesktop ? "1fr 1fr 1fr 1fr 1fr" : "1fr", gap: 2 }}>
                        <TextField
                            id="minAlertTime"
                            inputProps={{ "data-testid": "minAlertTime" }}
                            label="Minimum Alert Time"
                            value={minAlertTime}
                            onChange={(e) => handleIntegerFieldChange(e, setMinAlertTime)}
                            error={snackbar?.severity === "error" && (isNaN(parseFloat(minAlertTime)) || parseFloat(minAlertTime) < 0)}
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            autoComplete="off"
                            InputProps={{ endAdornment: <InputAdornment position="end">sec</InputAdornment> }}
                        />
                        <TextField
                            id="ownshipExpireTime"
                            inputProps={{ "data-testid": "ownshipExpireTime" }}
                            label="Ownship Expire Time"
                            value={ownshipExpire}
                            onChange={(e) => handleIntegerFieldChange(e, setOwnshipExpire)}
                            error={snackbar?.severity === "error" && (isNaN(parseFloat(ownshipExpire)) || parseFloat(ownshipExpire) < 0)}
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            autoComplete="off"
                            InputProps={{ endAdornment: <InputAdornment position="end">sec</InputAdornment> }}
                        />
                        <TextField
                            id="surveillanceExpireTime"
                            inputProps={{ "data-testid": "surveillanceExpireTime" }}
                            label="Surveillance Expire Time"
                            value={surveillanceExpire}
                            onChange={(e) => handleIntegerFieldChange(e, setSurveillanceExpire)}
                            error={snackbar?.severity === "error" && (isNaN(parseFloat(surveillanceExpire)) || parseFloat(surveillanceExpire) < 0)}
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            autoComplete="off"
                            InputProps={{ endAdornment: <InputAdornment position="end">sec</InputAdornment> }}
                        />
                        <TextField
                            id="secondaryShutoffTime"
                            inputProps={{ "data-testid": "secondaryShutoffTime" }}
                            label="Secondary Shutoff Time"
                            value={secondaryShutoff}
                            onChange={(e) => handleIntegerFieldChange(e, setSecondaryShutoff)}
                            error={snackbar?.severity === "error" && (isNaN(parseFloat(secondaryShutoff)) || parseFloat(secondaryShutoff) < 0)}
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            autoComplete="off"
                            InputProps={{ endAdornment: <InputAdornment position="end">sec</InputAdornment> }}
                        />
                    </Box>

                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox checked={disableAlertOnPositiveRangeRate} onChange={(e) => setDisableAlertOnPositiveRangeRate(e.target.checked)} />
                            }
                            label="Disable Alert on Positive Range Rate"
                            sx={{ width: "100%" }}
                        />
                    </Box>

                    <Divider textAlign="left" flexItem>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
                            Warning Alert Params <WarningAmberIcon sx={{ color: "#B96F0B" }} />
                        </Box>
                    </Divider>
                    <Box sx={{ display: "grid", gridTemplateColumns: isDesktop ? "1fr 1fr 1fr 1fr" : "1fr", gap: 2 }}>
                        <TextField
                            id="warningRadarHorz"
                            inputProps={{ "data-testid": "warningRadarHorz" }}
                            label="Radar Horizontal"
                            value={warningRadarHorz}
                            onChange={(e) => setWarningRadarHorz(e.target.value)}
                            error={
                                snackbar?.severity === "error" &&
                                (isNaN(parseFloat(warningRadarHorz)) ||
                                    parseFloat(warningRadarHorz) < 0 ||
                                    parseFloat(warningRadarHorz) > parseFloat(cautionRadarHorz))
                            }
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            type="number"
                            onWheel={(e) => e.target.blur()}
                            InputProps={{ endAdornment: <InputAdornment position="end">nm</InputAdornment> }}
                        />
                        <TextField
                            id="warningRadarVert"
                            inputProps={{ "data-testid": "warningRadarVert" }}
                            label="Radar Vertical"
                            value={warningRadarVert}
                            onChange={(e) => setWarningRadarVert(e.target.value)}
                            error={
                                snackbar?.severity === "error" &&
                                (isNaN(parseFloat(warningRadarVert)) ||
                                    parseFloat(warningRadarVert) < 0 ||
                                    parseFloat(warningRadarVert) > parseFloat(cautionRadarVert))
                            }
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            type="number"
                            onWheel={(e) => e.target.blur()}
                            InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
                        />
                        <TextField
                            id="warningAdsbHorz"
                            inputProps={{ "data-testid": "warningAdsbHorz" }}
                            label="ADSB Horizontal"
                            value={warningAdsbHorz}
                            onChange={(e) => setWarningAdsbHorz(e.target.value)}
                            error={
                                snackbar?.severity === "error" &&
                                (isNaN(parseFloat(warningAdsbHorz)) ||
                                    parseFloat(warningAdsbHorz) < 0 ||
                                    parseFloat(warningAdsbHorz) > parseFloat(cautionAdsbHorz))
                            }
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            type="number"
                            onWheel={(e) => e.target.blur()}
                            InputProps={{ endAdornment: <InputAdornment position="end">nm</InputAdornment> }}
                        />
                        <TextField
                            id="warningAdsbVert"
                            inputProps={{ "data-testid": "warningAdsbVert" }}
                            label="ADSB Vertical"
                            value={warningAdsbVert}
                            onChange={(e) => setWarningAdsbVert(e.target.value)}
                            error={
                                snackbar?.severity === "error" &&
                                (isNaN(parseFloat(warningAdsbVert)) ||
                                    parseFloat(warningAdsbVert) < 0 ||
                                    parseFloat(warningAdsbVert) > parseFloat(cautionAdsbVert))
                            }
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            type="number"
                            onWheel={(e) => e.target.blur()}
                            InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
                        />
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: isDesktop ? "1fr 1fr 1fr 1fr" : "1fr", gap: 2 }}>
                        <TextField
                            id="warningUasHorz"
                            inputProps={{ "data-testid": "warningUasHorz" }}
                            label="UAS Horizontal"
                            value={warningUasHorz}
                            onChange={(e) => setWarningUasHorz(e.target.value)}
                            error={
                                snackbar?.severity === "error" &&
                                (isNaN(parseFloat(warningUasHorz)) || parseFloat(warningUasHorz) < 0 || parseFloat(warningUasHorz) > parseFloat(cautionUasHorz))
                            }
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            type="number"
                            onWheel={(e) => e.target.blur()}
                            InputProps={{ endAdornment: <InputAdornment position="end">nm</InputAdornment> }}
                        />
                        <TextField
                            id="warningUasVert"
                            inputProps={{ "data-testid": "warningUasVert" }}
                            label="UAS Vertical"
                            value={warningUasVert}
                            onChange={(e) => setWarningUasVert(e.target.value)}
                            error={
                                snackbar?.severity === "error" &&
                                (isNaN(parseFloat(warningUasVert)) || parseFloat(warningUasVert) < 0 || parseFloat(warningUasVert) > parseFloat(cautionUasVert))
                            }
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            type="number"
                            onWheel={(e) => e.target.blur()}
                            InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
                        />
                        <TextField
                            id="warningProjection"
                            inputProps={{ "data-testid": "warningProjection" }}
                            label="Projection"
                            value={warningProjection}
                            onChange={(e) => handleIntegerFieldChange(e, setWarningProjection)}
                            error={snackbar?.severity === "error" && (isNaN(parseFloat(warningProjection)) || parseFloat(warningProjection) < 0)}
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            autoComplete="off"
                            InputProps={{ endAdornment: <InputAdornment position="end">sec</InputAdornment> }}
                        />
                    </Box>
                    <Divider textAlign="left" flexItem>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
                            Caution Alert Params <WarningAmberIcon sx={{ color: "#BEBB1F" }} />
                        </Box>
                    </Divider>
                    <Box sx={{ display: "grid", gridTemplateColumns: isDesktop ? "1fr 1fr 1fr 1fr" : "1fr", gap: 2 }}>
                        <TextField
                            id="cautionRadarHorz"
                            inputProps={{ "data-testid": "cautionRadarHorz" }}
                            label="Radar Horizontal"
                            value={cautionRadarHorz}
                            onChange={(e) => setCautionRadarHorz(e.target.value)}
                            error={
                                snackbar?.severity === "error" &&
                                (isNaN(parseFloat(cautionRadarHorz)) ||
                                    parseFloat(cautionRadarHorz) < 0 ||
                                    parseFloat(warningRadarHorz) > parseFloat(cautionRadarHorz))
                            }
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            type="number"
                            onWheel={(e) => e.target.blur()}
                            InputProps={{ endAdornment: <InputAdornment position="end">nm</InputAdornment> }}
                        />
                        <TextField
                            id="cautionRadarVert"
                            inputProps={{ "data-testid": "cautionRadarVert" }}
                            label="Radar Vertical"
                            value={cautionRadarVert}
                            onChange={(e) => setCautionRadarVert(e.target.value)}
                            error={
                                snackbar?.severity === "error" &&
                                (isNaN(parseFloat(cautionRadarVert)) ||
                                    parseFloat(cautionRadarVert) < 0 ||
                                    parseFloat(warningRadarVert) > parseFloat(cautionRadarVert))
                            }
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            type="number"
                            onWheel={(e) => e.target.blur()}
                            InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
                        />
                        <TextField
                            id="cautionAdsbHorz"
                            inputProps={{ "data-testid": "cautionAdsbHorz" }}
                            label="ADSB Horizontal"
                            value={cautionAdsbHorz}
                            onChange={(e) => setCautionAdsbHorz(e.target.value)}
                            error={
                                snackbar?.severity === "error" &&
                                (isNaN(parseFloat(cautionAdsbHorz)) ||
                                    parseFloat(cautionAdsbHorz) < 0 ||
                                    parseFloat(warningAdsbHorz) > parseFloat(cautionAdsbHorz))
                            }
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            type="number"
                            onWheel={(e) => e.target.blur()}
                            InputProps={{ endAdornment: <InputAdornment position="end">nm</InputAdornment> }}
                        />
                        <TextField
                            id="cautionAdsbVert"
                            inputProps={{ "data-testid": "cautionAdsbVert" }}
                            label="ADSB Vertical"
                            value={cautionAdsbVert}
                            onChange={(e) => setCautionAdsbVert(e.target.value)}
                            error={
                                snackbar?.severity === "error" &&
                                (isNaN(parseFloat(cautionAdsbVert)) ||
                                    parseFloat(cautionAdsbVert) < 0 ||
                                    parseFloat(warningAdsbVert) > parseFloat(cautionAdsbVert))
                            }
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            type="number"
                            onWheel={(e) => e.target.blur()}
                            InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
                        />
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: isDesktop ? "1fr 1fr 1fr 1fr" : "1fr", gap: 2 }}>
                        <TextField
                            id="cautionUasHorz"
                            inputProps={{ "data-testid": "cautionUasHorz" }}
                            label="UAS Horizontal"
                            value={cautionUasHorz}
                            onChange={(e) => setCautionUasHorz(e.target.value)}
                            error={
                                snackbar?.severity === "error" &&
                                (isNaN(parseFloat(cautionUasHorz)) || parseFloat(cautionUasHorz) < 0 || parseFloat(warningUasHorz) > parseFloat(cautionUasHorz))
                            }
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            type="number"
                            onWheel={(e) => e.target.blur()}
                            InputProps={{ endAdornment: <InputAdornment position="end">nm</InputAdornment> }}
                        />
                        <TextField
                            id="cautionUasVert"
                            inputProps={{ "data-testid": "cautionUasVert" }}
                            label="UAS Vertical"
                            value={cautionUasVert}
                            onChange={(e) => setCautionUasVert(e.target.value)}
                            error={
                                snackbar?.severity === "error" &&
                                (isNaN(parseFloat(cautionUasVert)) || parseFloat(cautionUasVert) < 0 || parseFloat(warningUasVert) > parseFloat(cautionUasVert))
                            }
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            type="number"
                            onWheel={(e) => e.target.blur()}
                            InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
                        />
                        <TextField
                            id="cautionProjection"
                            inputProps={{ "data-testid": "cautionProjection" }}
                            label="Projection"
                            value={cautionProjection}
                            onChange={(e) => handleIntegerFieldChange(e, setCautionProjection)}
                            error={snackbar?.severity === "error" && (isNaN(parseFloat(cautionProjection)) || parseFloat(cautionProjection) < 0)}
                            fullWidth
                            margin="dense"
                            sx={{ my: 0 }}
                            autoComplete="off"
                            InputProps={{ endAdornment: <InputAdornment position="end">sec</InputAdornment> }}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} id="close" data-testid="close" sx={{ mr: "auto" }}>
                    Close
                </Button>
                <Button onClick={handleReset} id="reset" data-testid="reset">
                    Reset
                </Button>
                <Button onClick={handleSave} id="save" data-testid="save">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function AddToolbar(props) {
    const { setEditRow, setZcaDialogOpen } = props;
    const { user } = useUserAuth();

    const handleClick = () => {
        setEditRow(getDefaultZcaConfig(user));
        setZcaDialogOpen(true);
    };

    return (
        <GridToolbarContainer>
            <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
                Add Record
            </Button>
        </GridToolbarContainer>
    );
}

export function getDefaultZcaConfig(user, id = 0, configUUID = uuidv4()) {
    return {
        id,
        config_uuid: configUUID,
        organization_id: user.organization_id,
        name: "",
        org_default: false,
        min_alert_time_sec: 8,
        ownship_expire_sec: 10,
        surveillance_expire_sec: 5,
        secondary_shutoff_sec: 600,
        lat_bound_range_deg: 1.0,
        lon_bound_range_deg: 1.0,
        alt_bound_range_m: 3048.0,
        m_of_n: [2, 4],
        alert_volumes_warning_radar_horz_m: 2222.4,
        alert_volumes_warning_radar_vert_m: 135.6,
        alert_volumes_warning_adsb_horz_m: 2222.4,
        alert_volumes_warning_adsb_vert_m: 135.6,
        alert_volumes_warning_uas_horz_m: 2222.4,
        alert_volumes_warning_uas_vert_m: 135.6,
        alert_volumes_warning_projection_sec: 0,
        alert_volumes_caution_radar_horz_m: 2778,
        alert_volumes_caution_radar_vert_m: 198.1,
        alert_volumes_caution_adsb_horz_m: 2778,
        alert_volumes_caution_adsb_vert_m: 198.1,
        alert_volumes_caution_uas_horz_m: 2778,
        alert_volumes_caution_uas_vert_m: 198.1,
        alert_volumes_caution_projection_sec: 0,
        disable_alert_on_positive_range_rate: false
    };
}
